"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { industries } from "@/lib/data/site";
import { SectionHeader } from "@/components/ui/section-header";
import { cn } from "@/lib/utils";

const visuals: Record<string, string> = {
  finance: "from-emerald-400/30 via-cyan-400/15 to-transparent",
  healthcare: "from-sky-400/30 via-violet-400/15 to-transparent",
  retail: "from-fuchsia-400/25 via-orange-300/15 to-transparent",
  manufacturing: "from-amber-400/25 via-slate-400/15 to-transparent",
  technology: "from-violet-400/30 via-cyan-400/15 to-transparent",
  logistics: "from-blue-400/30 via-cyan-300/15 to-transparent",
  education: "from-indigo-400/25 via-pink-300/15 to-transparent",
  public: "from-teal-400/25 via-blue-300/15 to-transparent",
  telecom: "from-purple-400/30 via-blue-400/15 to-transparent",
};

export function IndustryExplorer() {
  const [active, setActive] = useState(0);
  const current = industries[active];

  return (
    <section className="relative overflow-hidden py-20 sm:py-24 md:py-32">
      <div className="container-wide">
        <SectionHeader
          eyebrow="Industries"
          title={"DOMAIN CONTEXT.\nENTERPRISE PRECISION."}
          description="Hover an industry to shift the visual environment — each sector needs a distinct intelligence model."
        />

        <div className="mt-12 grid gap-5 sm:mt-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-6">
          <div className="h-scroll flex gap-2 pb-2 lg:block lg:space-y-2 lg:overflow-visible lg:pb-0">
            {industries.map((industry, index) => (
              <button
                key={industry.slug}
                type="button"
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                onClick={() => setActive(index)}
                className={cn(
                  "min-w-[200px] rounded-2xl border px-4 py-3 text-left transition lg:flex lg:w-full lg:min-w-0 lg:items-center lg:justify-between lg:py-4",
                  active === index
                    ? "border-accent/40 bg-card shadow-[var(--shadow-glow)]"
                    : "border-transparent hover:border-border",
                )}
              >
                <span className="font-medium">{industry.name}</span>
                <span className="ml-3 font-mono text-xs text-muted lg:ml-0">0{index + 1}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.slug}
              initial={{ opacity: 0, scale: 0.98, filter: "blur(8px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.99, filter: "blur(6px)" }}
              transition={{ duration: 0.4 }}
              className={cn(
                "relative min-h-[360px] overflow-hidden rounded-[1.5rem] border border-border bg-gradient-to-br p-6 sm:min-h-[420px] sm:rounded-[2rem] sm:p-8 md:p-10",
                visuals[current.visual],
              )}
            >
              <div className="absolute inset-0 bg-background/55" />
              <div className="absolute inset-0 grid-overlay opacity-60" />
              <div className="absolute -right-10 top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
              <div className="relative max-w-lg">
                <p className="eyebrow mb-4">Industry focus</p>
                <h3 className="font-display text-3xl tracking-tight sm:text-4xl md:text-5xl">{current.name}</h3>
                <p className="mt-5 leading-relaxed text-muted">{current.description}</p>
                <Link
                  href={`/industries/${current.slug}`}
                  className="mt-5 inline-flex text-sm font-medium text-accent underline-offset-4 hover:underline"
                >
                  Explore {current.name} solutions
                </Link>
                <div className="mt-8 h-36 rounded-3xl border border-border bg-card/50 backdrop-blur sm:mt-10 sm:h-40">
                  <div className="flex h-full items-end gap-1.5 p-4 sm:gap-2 sm:p-5">
                    {Array.from({ length: 14 }).map((_, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-accent/80 to-cyan/50"
                        initial={{ height: "18%" }}
                        animate={{ height: `${22 + ((i * 19) % 72)}%` }}
                        transition={{ duration: 0.85, delay: i * 0.035, repeat: Infinity, repeatType: "mirror", repeatDelay: 1.8 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}